import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import Breadcrumbs from '../components/Breadcrumbs';
import { useOrganization } from '../context/OrganizationContext';
import { useSubscription, UsageStats } from '../hooks/useSubscription';

// ponytail: gateway merges these blocks best-effort; each is optional
type ExtendedUsage = UsageStats & {
  emails?: { used: number; cap: number };
  identities?: { used: number; cap: number };
  accounts?: { used: number };
  runs?: { used: number; cap: number };
};

const Billing: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const { isOwner } = useOrganization();
  const {
    subscription,
    usage,
    isLoading: subscriptionLoading,
    cancelSubscription,
    resumeSubscription,
    refresh: refreshSubscription,
  } = useSubscription();

  const extendedUsage = usage as ExtendedUsage | null;

  const handleCancelSubscription = async () => {
    setIsCanceling(true);
    setCancelError(null);
    try {
      const result = await cancelSubscription();
      if (result.success) {
        setShowCancelModal(false);
        refreshSubscription();
      } else {
        setCancelError(result.error || 'Failed to cancel subscription. Please try again.');
      }
    } finally {
      setIsCanceling(false);
    }
  };

  const handleResumeSubscription = async () => {
    setIsResuming(true);
    try {
      const success = await resumeSubscription();
      if (success) {
        refreshSubscription();
      }
    } finally {
      setIsResuming(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="h-full overflow-hidden flex flex-col bg-white dark:bg-slate-950">
      <TopBar onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />

      <div className="flex flex-1 min-h-0 relative">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <div className="flex-1 overflow-y-auto relative">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Breadcrumbs items={[{ label: 'Usage & Billing' }]} className="mb-6" />

            {/* Header */}
            <div className="mb-8">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">Usage & Billing</h1>
              <p className="text-sm text-gray-500 dark:text-slate-400">Your Sente plan, usage, and subscription</p>
            </div>

            {/* Billing card */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden shadow-sm mb-6">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-800">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">Billing & Usage</h2>
                  {subscriptionLoading && (
                    <div className="w-4 h-4 border-2 border-gray-300 dark:border-slate-600 border-t-gray-600 dark:border-t-slate-300 rounded-full animate-spin" />
                  )}
                </div>
              </div>
              <div className="p-6">
                {subscription ? (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Current Plan */}
                    <div className="flex flex-col justify-between">
                      <div>
                        <div className="text-sm text-gray-500 dark:text-slate-400 mb-1">Current Plan</div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                          {subscription.comped ? 'Pilot' : subscription.planName}
                          {subscription.cancelAtPeriodEnd && (
                            <span className="text-xs px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full">
                              Canceling
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                          {subscription.planId === 'free' ? 'Period' : 'Billing period'}: {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                        </div>
                      </div>
                      <div className="mt-4">
                        {subscription.comped ? (
                          <a
                            href="mailto:support@sente.run"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            Contact us
                          </a>
                        ) : (
                          <Link
                            to="/pricing"
                            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            {subscription.planId === 'free' ? 'Upgrade Plan' : 'Change Plan'}
                          </Link>
                        )}
                      </div>
                    </div>

                    {/* Usage */}
                    {usage && (
                      <div className="lg:col-span-2 lg:border-l lg:border-gray-100 lg:dark:border-slate-800 lg:pl-6">
                        <div className="text-sm text-gray-500 dark:text-slate-400 mb-2">Usage This Period</div>
                        {extendedUsage?.identities && (
                          <>
                            <div className="flex items-end justify-between mb-2">
                              <div className="text-3xl font-bold text-gray-900 dark:text-white">
                                {extendedUsage.identities.used}
                              </div>
                              <div className="text-sm text-gray-500 dark:text-slate-400">
                                of {extendedUsage.identities.cap} identities
                              </div>
                            </div>
                            {/* Progress bar */}
                            <div className="h-3 bg-gray-100 dark:bg-slate-800 rounded-full overflow-hidden mb-2">
                              <div
                                className={`h-full rounded-full transition-all ${
                                  extendedUsage.identities.used >= extendedUsage.identities.cap
                                    ? 'bg-red-500'
                                    : (extendedUsage.identities.used / extendedUsage.identities.cap) * 100 > 80
                                    ? 'bg-yellow-500'
                                    : 'bg-blue-500'
                                }`}
                                style={{ width: `${Math.min((extendedUsage.identities.used / extendedUsage.identities.cap) * 100, 100)}%` }}
                              />
                            </div>
                          </>
                        )}
                        <div className="flex items-center justify-end text-sm">
                          <span className="text-gray-500 dark:text-slate-400">
                            Resets {formatDate(usage.periodEnd)}
                          </span>
                        </div>
                        {usage.isOverLimit && (
                          <p className="mt-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
                            Your plan's included runtime for this month is used up — upgrade your plan or contact us.
                          </p>
                        )}
                        {extendedUsage?.runs && extendedUsage.runs.used >= extendedUsage.runs.cap && (
                          <p className="mt-3 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">
                            Monthly registrations & logins limit reached — upgrade your plan.
                          </p>
                        )}

                        {/* Sente usage rows — gateway merges these best-effort, render only when present */}
                        {(extendedUsage?.accounts || extendedUsage?.runs || extendedUsage?.emails) && (
                          <div className="mt-4 pt-3 border-t border-gray-100 dark:border-slate-800 space-y-3">
                            {extendedUsage?.accounts && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600 dark:text-slate-400">Accounts under management</span>
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {extendedUsage.accounts.used}
                                </span>
                              </div>
                            )}
                            {extendedUsage?.runs && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600 dark:text-slate-400">Registrations & logins</span>
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {extendedUsage.runs.used} of {extendedUsage.runs.cap}
                                </span>
                              </div>
                            )}
                            {extendedUsage?.emails && (
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-gray-600 dark:text-slate-400">Emails this month</span>
                                <span className="font-medium text-gray-900 dark:text-white">
                                  {extendedUsage.emails.used} of {extendedUsage.emails.cap}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
                      {subscriptionLoading ? 'Loading subscription...' : 'No subscription found'}
                    </p>
                    {!subscriptionLoading && (
                      <Link
                        to="/pricing"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        View Plans
                      </Link>
                    )}
                  </div>
                )}

                {/* Cancel/Resume Subscription - only for paid (non-comped) plans and organization owners */}
                {subscription && subscription.planId !== 'free' && !subscription.comped && isOwner && (
                  <div className="mt-6 pt-5 border-t border-gray-100 dark:border-slate-800">
                    {subscription.cancelAtPeriodEnd ? (
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500 dark:text-slate-400">
                          Your subscription will end on {formatDate(subscription.currentPeriodEnd)}
                        </div>
                        <button
                          onClick={handleResumeSubscription}
                          disabled={isResuming}
                          className="px-4 py-2 bg-gray-900 dark:bg-blue-600 hover:bg-gray-800 dark:hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                        >
                          {isResuming ? 'Resuming...' : 'Resume Subscription'}
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => { setCancelError(null); setShowCancelModal(true); }}
                        className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
                      >
                        Cancel Subscription
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowCancelModal(false)} />
          <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Cancel Subscription
            </h3>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
              Are you sure you want to cancel your subscription? You'll continue to have access until the end of your current billing period ({subscription && formatDate(subscription.currentPeriodEnd)}), after which you'll be downgraded to the Free plan.
            </p>
            {cancelError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-700 dark:text-red-400">{cancelError}</p>
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={isCanceling}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={isCanceling}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {isCanceling ? 'Canceling...' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
